// src/modules/app-views/_apps/appmulti/quotations/Form/Form.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useState } from 'react';
import { Card, Flex, Modal, Row, Spin, Steps } from 'antd';
import dayjs from 'dayjs';
import { MdFlightTakeoff } from 'react-icons/md';
import { clone } from 'utils/helpers';
import translateX from 'utils/translateX';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import appService from 'services/appService';
import appmultiService from 'modules/app-views/_apps/appmulti/services/appmultiService';
import SimpleButton from 'components/SimpleComponents/SimpleButton';
import Icon from 'components/JarvislyComponents/Icon';
import MultiQuotationsCheckoutStep1 from './Steps/CheckoutStep1';
import MultiQuotationsCheckoutStep2 from './Steps/CheckoutStep2';
import MultiQuotationsCheckoutStep3 from './Steps/CheckoutStep3';
import { CardPlan } from '../Components';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const chosenPlanTitleStyle = { color: 'darkcyan' };
const chosenPlanTitleTextStyle = { marginLeft: 8 };
const closeBtnStyle = { width: 130 };
const hiddenButtonStyle = { display: 'none' };

const stepsBlueprint = [
  { title: 'travelers', description: 'included_passengers' },
  { title: 'buyer', description: 'customer_data' },
  { title: 'finish', description: 'order_generation' },
];

// *******************************************************************************
// COMPONENT
// *******************************************************************************
const MultiQuotationCheckoutModal = props => {
  // HOOKS
  const {
    showModal,
    setShowModal,
    record,
    documentContext,
    travellers,
    setTravellers,
  } = props;

  const { filter } = documentContext || {};
  const isDone = record?.status === 'generated_order';

  // STATE
  const [step, setStep] = useState(0);
  const [enabledSteps, setEnabledSteps] = useState([0]);
  const [plan, setPlan] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [order, setOrder] = useState(null);
  const [adminAdjustment, setAdminAdjustment] = useState(null);
  const [adminDiscount, setAdminDiscount] = useState(null);
  const [emergencyContacts, setEmergencyContacts] = useState(null);
  const [paymentSelected, setPaymentSelected] = useState(null);
  const [creditCardInstallment, setCreditCardInstallment] = useState('1X');

  const isMobile = appService.isMobile();
  const isDisabled = !!order?._id;

  // HELPERS
  const buildTravellers = (forceClear = false) => {
    const out = [];
    if (!filter?.passengers) return out;
    filter.passengers.forEach((age, idx) => {
      if (!isNaN(age) || forceClear) {
        const mockBirthdate = dayjs(filter.departure || dayjs()).subtract(
          age,
          'years',
        );
        const foundIdx = travellers?.findIndex(t => t?.key === idx);
        let mock = {
          key: idx,
          age,
          birthdate: mockBirthdate,
          cpf: '',
          gender: null,
          name: `${translateX('passenger')} ${idx + 1}`,
          backColor: ['#', 'b9bcc4'].join(''),
          foreColor: ['#', 'ffffff'].join(''),
        };
        if (foundIdx > -1) mock = travellers[foundIdx];
        out.push(mock);
      }
    });
    return out;
  };

  // EFFECTS
  useEffect(() => {
    if (filter?.passengers) setTravellers(buildTravellers());
  }, [filter?.passengers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (record && filter?.days && travellers.length > 0) {
      const p = appmultiService.decoratePlan({
        plan: clone(record),
        days: filter.days,
        passengersArr: travellers,
        adminAdjustment,
        adminDiscount,
      });
      setPlan({ ...p });
    }
  }, [record, travellers]); // eslint-disable-line react-hooks/exhaustive-deps

  // HANDLERS
  const onClose = () => setShowModal(false);

  const onAfterClose = () => {
    if (order) {
      setOrder(null);
      setTravellers(buildTravellers(true));
      setStep(0);
      setPlan(null);
      setEnabledSteps([0]);
      setAdminAdjustment(null);
      setAdminDiscount(null);
    }
  };

  // RENDER
  const childProps = {
    ...props,
    step,
    setStep,
    setPlan,
    plan,
    filter,
    isMobile,
    setEnabledSteps,
    enabledSteps,
    isGenerating,
    setIsGenerating,
    setUpdating,
    updating,
    onClose,
    order,
    setOrder,
    isDisabled,
    setAdminAdjustment,
    setAdminDiscount,
    emergencyContacts,
    setEmergencyContacts,
    paymentSelected,
    setPaymentSelected,
    creditCardInstallment,
    setCreditCardInstallment,
  };

  const stepItems = stepsBlueprint.map((s, idx) => ({
    title: translateX(s.title),
    content: translateX(s.description),
    disabled: !isDone && !enabledSteps.includes(idx),
  }));

  return (
    <Modal
      closable={!isGenerating}
      cancelButtonProps={{ style: hiddenButtonStyle }}
      okButtonProps={{ style: hiddenButtonStyle }}
      width={960}
      okText={translateX('close')}
      mask={{ closable: false }}
      open={showModal}
      onCancel={onClose}
      onOk={onClose}
      afterClose={onAfterClose}
      destroyOnHidden={false}
      className="modal-content-padding-bottom-6"
    >
      <Spin spinning={isGenerating} size="large">
        <Row gutter={ROW_GUTTER}>
          <Card className="w-100 no-shadow" variant="borderless">
            <Steps
              orientation="horizontal"
              size="small"
              current={step}
              className="no-max-width-for-description"
              onChange={v => setStep(v)}
              items={stepItems}
            />
          </Card>
        </Row>

        <Row gutter={ROW_GUTTER}>
          <CardPlan
            {...childProps}
            plan={plan}
            profile="checkout"
            title={
              <Flex align="center" style={chosenPlanTitleStyle}>
                <div>
                  <Icon type={MdFlightTakeoff} />
                </div>
                <div style={chosenPlanTitleTextStyle}>
                  {translateX('chosen_plan')}
                </div>
              </Flex>
            }
          />
        </Row>

        <Row gutter={ROW_GUTTER}>
          {step === 0 ? <MultiQuotationsCheckoutStep1 {...childProps} /> : null}
          {step === 1 ? <MultiQuotationsCheckoutStep2 {...childProps} /> : null}
          {step === 2 ? <MultiQuotationsCheckoutStep3 {...childProps} /> : null}
        </Row>
      </Spin>

      {isDisabled ? (
        <Flex justify={isMobile ? 'center' : 'flex-end'} align="center">
          <SimpleButton
            title="close"
            type="default"
            style={closeBtnStyle}
            onClick={onClose}
          />
        </Flex>
      ) : null}
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default MultiQuotationCheckoutModal;
